import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { lightTheme, darkTheme } from "../theme";

type ThemeMode = "light" | "dark" | "system";

interface ThemeModeContextValue {
  mode: ThemeMode;
  resolvedMode: "light" | "dark";
  toggleMode: () => void;
  setForceLight: (force: boolean) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: "light",
  resolvedMode: "light",
  toggleMode: () => {},
  setForceLight: () => {},
});

const STORAGE_KEY = "haven-theme-mode";

function getSystemPreference(): "light" | "dark" {
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

function resolveMode(mode: ThemeMode): "light" | "dark" {
  return mode === "system" ? getSystemPreference() : mode;
}

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "light" || stored === "dark") return stored;
    } catch {}
    return "light";
  });

  const [systemPref, setSystemPref] = useState<"light" | "dark">(getSystemPreference);
  const [forceLight, setForceLight] = useState(false);

  // Listen for system preference changes
  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemPref(e.matches ? "dark" : "light");
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {}
  }, [mode]);

  const resolvedMode = forceLight ? "light" : (mode === "system" ? systemPref : mode);

  const toggleMode = useCallback(() => {
    setMode((prev) => {
      const current = resolveMode(prev);
      return current === "light" ? "dark" : "light";
    });
  }, []);

  const theme = useMemo(() => (resolvedMode === "dark" ? darkTheme : lightTheme), [resolvedMode]);

  // Remove dark-preload class from index.html once React has taken over theming
  useEffect(() => {
    document.documentElement.classList.remove("dark-preload");
  }, []);

  const value = useMemo(() => ({ mode, resolvedMode, toggleMode, setForceLight }), [mode, resolvedMode, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
